/**
 * IT-PAGINATION-001 — shared premium pagination for all IT module lists.
 * "Showing X–Y of Z records" + numbered pages w/ ellipsis + rows-per-page.
 */

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './ITPagination.css';

const buildPages = (page, pages) => {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const out = [1];
  if (page > 3) out.push('…');
  for (let p = Math.max(2, page - 1); p <= Math.min(pages - 1, page + 1); p++) out.push(p);
  if (page < pages - 2) out.push('…');
  out.push(pages);
  return out;
};

const ITPagination = ({ page, limit, total, onPageChange, onLimitChange, testid = 'it-pagination' }) => {
  const pages = Math.max(1, Math.ceil((total || 0) / limit));
  if (!total || total <= 0) return null;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div className="itp-bar" data-testid={testid}>
      <span className="itp-info" data-testid={`${testid}-info`}>
        Showing {start}–{end} of {total} record{total === 1 ? '' : 's'}
      </span>
      <div className="itp-controls">
        {onLimitChange && (
          <label className="itp-rows">
            Rows
            <select
              value={limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              data-testid={`${testid}-limit`}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
        )}
        <div className="itp-pager">
          <button
            className="itp-btn"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            data-testid={`${testid}-prev`}
            aria-label="Previous page"
          >
            <ChevronLeft size={14} />
          </button>
          {buildPages(page, pages).map((p, i) =>
            p === '…' ? (
              <span key={`e${i}`} className="itp-ellipsis">…</span>
            ) : (
              <button
                key={p}
                className={`itp-btn itp-num ${p === page ? 'active' : ''}`}
                onClick={() => p !== page && onPageChange(p)}
              >
                {p}
              </button>
            )
          )}
          <button
            className="itp-btn"
            disabled={page >= pages}
            onClick={() => onPageChange(page + 1)}
            data-testid={`${testid}-next`}
            aria-label="Next page"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ITPagination;
